import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReelPlayer from "../components/ReelPlayer";
import { API } from "../lib/api";
import { Film, Globe } from "lucide-react";

/**
 * Public (no-auth) white-label shared reel viewer.
 * URL pattern: /w/:slug/:reelId
 * Shows the reel with the agency branding instead of Reelio branding.
 */
const PublicSharedReel = () => {
  const { slug, reelId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/share/${slug}/${reelId}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Reel non trovato"));
  }, [slug, reelId]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-sm border border-zinc-800 flex items-center justify-center">
            <Film className="w-6 h-6 text-zinc-600" />
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter text-white mb-3">
            Reel non disponibile
          </h1>
          <p className="text-zinc-400 font-body mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-800 hover:border-zinc-700 text-zinc-300 font-body rounded-sm transition-all"
          >
            Reelio
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="flex items-center gap-3 text-zinc-500">
          <span className="w-2 h-2 rounded-full bg-[#FF4400] pulse-dot" />
          <span className="font-mono text-xs tracking-widest uppercase">Loading...</span>
        </div>
      </div>
    );
  }

  const { workspace, reel } = data;
  const accent = workspace.agency_color_primary || "#FF4400";

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Custom agency header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-sm flex items-center justify-center font-display font-black text-white text-sm overflow-hidden"
              style={{ backgroundColor: accent }}
              data-testid="shared-agency-logo"
            >
              {workspace.agency_logo ? (
                <img
                  src={`data:image/png;base64,${workspace.agency_logo}`}
                  alt={workspace.agency_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                workspace.agency_name?.[0]?.toUpperCase() || "A"
              )}
            </div>
            <div>
              <div className="font-display font-black text-white text-lg tracking-tighter leading-none" data-testid="shared-agency-name">
                {workspace.agency_name}
              </div>
              {workspace.agency_tagline && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  {workspace.agency_tagline}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 border border-zinc-800 bg-zinc-950 rounded-sm">
            <Globe className="w-3 h-3 text-[#FF4400]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              /w/{workspace.agency_slug}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex justify-center items-start lg:sticky lg:top-24 self-start">
            <ReelPlayer reel={reel} />
          </div>

          <div className="lg:col-span-3">
            <div
              className="font-mono text-[11px] uppercase tracking-[0.2em] mb-3"
              style={{ color: accent }}
              data-testid="shared-reel-category"
            >
              · Anteprima Reel · {reel.category}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-4" data-testid="shared-reel-title">
              {reel.title}
            </h1>
            <p className="text-zinc-400 font-body text-lg mb-10">
              <span style={{ color: accent }}>Hook:</span> {reel.hook}
            </p>

            <div className="space-y-5">
              <Section label="Call To Action" accent={accent}>
                <div className="text-white font-display font-bold">{reel.cta}</div>
              </Section>
              <Section label="Caption" accent={accent}>
                <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-body">{reel.caption}</pre>
              </Section>
              <Section label="Hashtag" accent={accent}>
                <div className="flex flex-wrap gap-1.5">
                  {reel.hashtags.map((h) => (
                    <span
                      key={h}
                      className="px-2 py-1 text-xs font-mono border rounded-sm"
                      style={{ color: accent, backgroundColor: `${accent}1a`, borderColor: `${accent}66` }}
                    >
                      #{h}
                    </span>
                  ))}
                </div>
              </Section>
              <Section label="Musica suggerita" accent={accent}>
                <div className="text-zinc-300 font-body">{reel.music_mood}</div>
              </Section>
            </div>

            <div
              className="mt-12 pt-8 border-t border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-700"
              data-testid="shared-footer"
            >
              · Creato con ♥ da {workspace.agency_name} ·
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ label, children, accent }) => (
  <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
    <div className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: accent }}>
      {label}
    </div>
    {children}
  </div>
);

export default PublicSharedReel;
